import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiX, FiSearch, FiExternalLink, FiShield } from 'react-icons/fi';
import { authAPI, usersAPI } from '../../services/api';

function ImpersonateUserModal({ onClose }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [switchingId, setSwitchingId] = useState(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const q = query.trim();
    if (q.length < 2) {
      setUsers([]);
      setLoading(false);
      return;
    }

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    usersAPI
      .list({ search: q, limit: 20, isActive: 'true' })
      .then((res) => {
        if (ctrl.signal.aborted) return;
        const list = (res.data?.users || []).filter(u => u.role !== 'tenant_owner');
        setUsers(list);
        setLoading(false);
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        setError(err.response?.data?.error || t('impersonation.searchFailed'));
        setLoading(false);
      });

    return () => ctrl.abort();
  }, [query, t]);

  const handleSwitch = async (user) => {
    setError(null);
    // Open the tab synchronously in the click handler so the browser treats
    // it as user-initiated. We redirect it once the handoff token arrives.
    const newTab = window.open('about:blank', '_blank');
    if (!newTab) {
      setError(t('impersonation.popupBlocked'));
      return;
    }
    setSwitchingId(user._id);
    try {
      const res = await authAPI.impersonate(user._id);
      const url = `/impersonate/handoff?token=${encodeURIComponent(res.data.handoffToken)}`;
      newTab.location.href = url;
      // Sever the opener reference so the new tab cannot navigate this tab
      // (tabnabbing). We kept `window.open` without `noopener` so we could
      // redirect newTab above; this line restores the safety property.
      try { newTab.opener = null; } catch { /* ignore — cross-origin, benign */ }
      onClose();
    } catch (err) {
      try { newTab.close(); } catch { /* ignore */ }
      setError(err.response?.data?.error || t('impersonation.switchFailed'));
      setSwitchingId(null);
    }
  };

  const roleLabel = (role) =>
    role ? role.split('_').map((w) => w[0].toUpperCase() + w.slice(1)).join(' ') : '';

  return (
    <div
      className="impersonate-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="impersonate-modal-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="impersonate-modal__panel">
        <div className="impersonate-modal__head">
          <div className="impersonate-modal__head-text">
            <div id="impersonate-modal-title" className="impersonate-modal__title">
              {t('impersonation.modalTitle')}
            </div>
            <div className="impersonate-modal__subtitle">
              {t('impersonation.modalSubtitle')}
            </div>
          </div>
          <button
            type="button"
            className="impersonate-modal__close"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="impersonate-modal__search">
          <input
            ref={inputRef}
            type="text"
            className="impersonate-modal__search-input"
            placeholder={t('impersonation.searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
        </div>

        {error && <div className="impersonate-modal__error">{error}</div>}

        <div className="impersonate-modal__list">
          {query.trim().length < 2 ? (
            <div className="impersonate-modal__empty">
              <FiSearch size={18} style={{ marginBottom: 6, opacity: 0.5 }} />
              <div>{t('impersonation.startTyping')}</div>
            </div>
          ) : loading ? (
            <div className="impersonate-modal__empty">{t('common.loading')}</div>
          ) : users.length === 0 ? (
            <div className="impersonate-modal__empty">{t('impersonation.noResults')}</div>
          ) : (
            users.map((u) => (
              <button
                key={u._id}
                type="button"
                className="impersonate-modal__item"
                onClick={() => handleSwitch(u)}
                disabled={switchingId === u._id}
              >
                <div className="impersonate-modal__item-avatar">
                  {u.firstName?.[0]}{u.lastName?.[0]}
                </div>
                <div className="impersonate-modal__item-info">
                  <div className="impersonate-modal__item-name">
                    {u.firstName} {u.lastName}
                  </div>
                  <div className="impersonate-modal__item-email">{u.email}</div>
                </div>
                <span className="impersonate-modal__item-role">{roleLabel(u.role)}</span>
              </button>
            ))
          )}
        </div>

        <div className="impersonate-modal__foot">
          <FiShield />
          <span>{t('impersonation.auditNote')}</span>
          <FiExternalLink style={{ marginLeft: 'auto' }} />
        </div>
      </div>
    </div>
  );
}

export default ImpersonateUserModal;
